/**
 * cardioAudio — chargement et lecture des clips vocaux du minuteur Cardio (voix humaine
 * pré-enregistrée, OpenAI TTS, servie statiquement). Cache mémoire des balises <audio> pour
 * une lecture instantanée. Manifeste structuré par voix ET par langue : voices[voice][lang][key].
 */
const backendUrl = process.env.REACT_APP_BACKEND_URL;
let manifestCache = null;
let manifestPromise = null;
const audioCache = {};

export const CARDIO_VOICES = [
  { value: 'male', label: 'Voix masculine' },
  { value: 'female', label: 'Voix féminine' },
];

const langOf = (en) => (en ? 'en' : 'fr');

export const loadCardioAudioManifest = (authFetch) => {
  if (manifestCache) return Promise.resolve(manifestCache);
  if (manifestPromise) return manifestPromise;
  manifestPromise = authFetch('/api/tracking/cardio-pro/audio-manifest')
    .then((r) => r.json())
    .then((d) => {
      manifestCache = d.voices || {};
      preloadCardioAudio('male', 'fr'); preloadCardioAudio('female', 'fr');
      preloadCardioAudio('male', 'en'); preloadCardioAudio('female', 'en');
      return manifestCache;
    })
    .catch(() => ({}));
  return manifestPromise;
};

export const preloadCardioAudio = (voice, lang = 'fr') => {
  const manifest = manifestCache?.[voice]?.[lang];
  if (!manifest) return;
  Object.entries(manifest).forEach(([key, url]) => {
    const cacheKey = `${voice}:${lang}:${key}`;
    if (!audioCache[cacheKey]) {
      const audio = new Audio(`${backendUrl}${url}`);
      audio.preload = 'auto';
      audioCache[cacheKey] = audio;
    }
  });
};

/** Joue le clip vocal (ex: 'three', 'go', 'rest'...) dans la voix + langue choisies — utilise un
 * clone de l'élément <audio> à chaque appel (au lieu de réutiliser/rembobiner le même élément) pour
 * éviter les échecs de lecture silencieux quand un appel précédent est encore en cours de lecture
 * ou pas totalement chargé (cause du bug "parfois le son ne joue pas") — no-op silencieux si le
 * manifeste n'est pas encore chargé ou si la lecture est bloquée par le navigateur. */
export const playCardioVoice = (voice, key, en = false) => {
  const lang = langOf(en);
  const manifest = manifestCache?.[voice]?.[lang];
  if (!manifest || !manifest[key]) return;
  const cacheKey = `${voice}:${lang}:${key}`;
  let base = audioCache[cacheKey];
  if (!base) {
    base = new Audio(`${backendUrl}${manifest[key]}`);
    audioCache[cacheKey] = base;
  }
  let audio;
  try { audio = base.cloneNode(true); } catch { audio = base; }
  try { audio.currentTime = 0; } catch { /* pas encore chargé, on tente quand même la lecture */ }
  try {
    const p = audio.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch { /* audio indisponible */ }
};

/** Débloque TOUS les clips (voix masculine ET féminine, langue courante) dès le premier geste
 * utilisateur (clic "Démarrer") — certains navigateurs mobiles (iOS Safari) exigent un déblocage
 * PAR élément <audio>, pas un seul déblocage global ; débloquer une seule voix laissait l'autre
 * muette après un changement de voix en cours de séance (cause probable du bug intermittent). */
export const unlockCardioAudio = (voice, en = false) => {
  const lang = langOf(en);
  ['male', 'female'].forEach((v) => {
    const manifest = manifestCache?.[v]?.[lang];
    if (!manifest) return;
    Object.entries(manifest).forEach(([key, url]) => {
      const cacheKey = `${v}:${lang}:${key}`;
      let audio = audioCache[cacheKey];
      if (!audio) { audio = new Audio(`${backendUrl}${url}`); audioCache[cacheKey] = audio; }
      audio.volume = 0;
      const p = audio.play();
      if (p && typeof p.then === 'function') {
        p.then(() => { audio.pause(); audio.currentTime = 0; audio.volume = 1; }).catch(() => { audio.volume = 1; });
      }
    });
  });
};
